import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useWeb3Context } from '../../contexts/web3Context'
import {
  getCastleConquerRequests,
  approveCastleConquerRequest,
  declineCastleConquerRequest,
  type CastleConquerRequestItem,
} from '../../common/allianceApi'
import { AllianceConquerRequestModal } from './AllianceConquerRequestModal'
import { alert } from '../Alert/Alert'

const POLL_MS = 30 * 1000

/**
 * Global watcher (mounted once in App). When the connected wallet leads an alliance and a member has
 * requested to CONQUER a castle (post-victory), it shows the leader a confirm-style approval modal.
 * Approving joins the castle to the alliance (reversing the winner's raid loot). Also refreshes on the
 * `alliance-conquer-request` window event that App fires from the `alliance:conquer-request` socket.
 */
export const AllianceConquerRequestWatcher: React.FC = () => {
  const { address } = useWeb3Context()
  const [requests, setRequests] = useState<CastleConquerRequestItem[]>([])
  const [allianceName, setAllianceName] = useState<string | undefined>(undefined)
  const [dismissed, setDismissed] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const prevCount = useRef(0)

  const refresh = useCallback(async () => {
    if (!address) {
      setRequests([])
      return
    }
    try {
      const res = await getCastleConquerRequests(address)
      if (res.isLeader) {
        setAllianceName(res.allianceName)
        setRequests(res.requests || [])
        if ((res.requests?.length || 0) > prevCount.current) setDismissed(false)
        prevCount.current = res.requests?.length || 0
      } else {
        setRequests([])
        prevCount.current = 0
      }
    } catch {
      setRequests([])
    }
  }, [address])

  useEffect(() => {
    void refresh()
    if (!address) return
    const id = window.setInterval(() => void refresh(), POLL_MS)
    const onNudge = () => void refresh()
    window.addEventListener('alliance-conquer-request', onNudge)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('alliance-conquer-request', onNudge)
    }
  }, [address, refresh])

  const lastAddress = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (lastAddress.current !== address) {
      lastAddress.current = address
      setDismissed(false)
      prevCount.current = 0
    }
  }, [address])

  const handleApprove = useCallback(
    async (requestId: string) => {
      if (!address) return
      setBusyId(requestId)
      try {
        const res = await approveCastleConquerRequest(address, requestId)
        // Recruiting can still be impossible at approval time (the roster filled up meanwhile) —
        // the server then cancels everything and returns the plunder, so say so.
        if (res?.recruitFailed) alert.open(res.message || 'The castle could not be recruited.')
        await refresh()
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } }
        alert.open(err?.response?.data?.message || 'Could not approve the conquest.')
      } finally {
        setBusyId(null)
      }
    },
    [address, refresh],
  )

  const handleDecline = useCallback(
    async (requestId: string) => {
      if (!address) return
      setBusyId(requestId)
      try {
        await declineCastleConquerRequest(address, requestId)
        await refresh()
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } }
        alert.open(err?.response?.data?.message || 'Could not decline the conquest.')
      } finally {
        setBusyId(null)
      }
    },
    [address, refresh],
  )

  const open = !!address && !dismissed && requests.length > 0

  return (
    <AllianceConquerRequestModal
      open={open}
      allianceName={allianceName}
      requests={requests}
      busyId={busyId}
      onApprove={handleApprove}
      onDecline={handleDecline}
      onClose={() => setDismissed(true)}
    />
  )
}

export default AllianceConquerRequestWatcher
